package com.schoolconnect.app.repository.schoolstaff

import com.schoolconnect.app.datasource.network.KtorApiClient
import com.schoolconnect.app.datasource.network.request.SchoolStaffCreateRequest
import com.schoolconnect.app.datasource.network.request.AddStaffWithAssignmentsRequest
import com.schoolconnect.app.datasource.network.request.SchoolStaffUpdateRequest
import com.schoolconnect.app.datasource.network.request.UpdateStaffWithAssignmentsRequest
import com.schoolconnect.app.datasource.network.request.SchoolStaffFilterRequest
import com.schoolconnect.app.datasource.network.response.SchoolUsersAnalyticsResponse
import com.schoolconnect.app.datasource.network.response.SchoolStaffResponse
import com.schoolconnect.app.datasource.network.response.SchoolStaffWithAssignmentsResponse
import com.schoolconnect.app.datasource.network.response.serverTotalCountOrNull
import com.schoolconnect.app.datasource.network.response.withServerTotalCount
import com.schoolconnect.app.repository.common.CachedValue
import com.schoolconnect.app.repository.common.loadPagedItems
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.feature.common.ServerSort
import com.schoolconnect.app.repository.common.loadRefreshableCachedValue
import com.schoolconnect.app.repository.common.returnCachedAndRefresh
import com.schoolconnect.app.repository.common.tenantCacheKey
import com.schoolconnect.app.util.scLog
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob

object SchoolStaffRepository {
    private const val TAG = "[DATA_RETRIEVE_SC] SchoolStaffRepository"
    private const val PAGE_SIZE = 100
    private const val MAX_PAGES = 100

    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)

    private data class CachedSchoolStaff(
        val schoolId: String,
        val staff: List<SchoolStaffResponse>
    )

    private data class SchoolStaffPageCacheKey(
        val schoolId: String,
        val query: String,
        val roleTitle: String?,
        val isActive: Boolean?,
        val pageIndex: Int,
        val pageSize: Int,
        val sort: String
    )

    private data class CachedSchoolStaffPage(
        val key: SchoolStaffPageCacheKey,
        val staff: List<SchoolStaffResponse>
    )

    private var cachedSchoolStaff: CachedSchoolStaff? = null
    private var cachedSchoolStaffPage: CachedSchoolStaffPage? = null
    private val cachedAnalytics = mutableMapOf<String, CachedValue<SchoolUsersAnalyticsResponse>>()

    suspend fun loadSchoolStaffs(
        schoolId: String,
        forceRemote: Boolean = false
    ): Result<List<SchoolStaffResponse>> {
        val normalizedSchoolId = schoolId.trim()
        if (normalizedSchoolId.isBlank()) {
            return Result.failure(IllegalArgumentException("schoolId is required"))
        }

        cachedSchoolStaff?.takeIf { !forceRemote && it.schoolId == normalizedSchoolId }?.let { cache ->
            scLog(
                "$TAG loadSchoolStaffs cacheHit school=$normalizedSchoolId count=${cache.staff.size}"
            )
            return Result.success(cache.staff)
        }

        scLog("$TAG loadSchoolStaffs start school=$normalizedSchoolId forceRemote=$forceRemote")
        return runCatching {
            val rows = loadPagedItems(
                tag = TAG,
                operation = "loadSchoolStaffs",
                pageSize = PAGE_SIZE,
                maxPages = MAX_PAGES,
                startContext = "school=$normalizedSchoolId"
            ) { offset ->
                val page = KtorApiClient.getSchoolStaffs(
                    schoolId = normalizedSchoolId,
                    limit = PAGE_SIZE,
                    offset = offset
                ).getOrElse { throwable ->
                    scLog("$TAG loadSchoolStaffs pageFailure school=$normalizedSchoolId offset=$offset", throwable)
                    throw throwable
                }
                scLog("$TAG loadSchoolStaffs page school=$normalizedSchoolId offset=$offset count=${page.size}")
                page
            }.distinctBy { it.id }

            cachedSchoolStaff = CachedSchoolStaff(normalizedSchoolId, rows)
            scLog("$TAG loadSchoolStaffs complete school=$normalizedSchoolId count=${rows.size}")
            rows
        }.onFailure { throwable ->
            scLog("$TAG loadSchoolStaffs failure school=$normalizedSchoolId", throwable)
        }
    }

    suspend fun loadSchoolTeachers(
        schoolId: String,
        forceRemote: Boolean = false
    ): Result<List<SchoolStaffResponse>> {
        val normalizedSchoolId = schoolId.trim()
        if (normalizedSchoolId.isBlank()) return Result.failure(IllegalArgumentException("schoolId is required"))
        scLog("$TAG loadSchoolTeachers start school=$normalizedSchoolId forceRemote=$forceRemote")
        return runCatching {
            loadPagedItems(
                tag = TAG,
                operation = "loadSchoolTeachers",
                pageSize = PAGE_SIZE,
                maxPages = MAX_PAGES,
                startContext = "school=$normalizedSchoolId"
            ) { offset ->
                KtorApiClient.getSchoolTeachers(normalizedSchoolId, limit = PAGE_SIZE, offset = offset)
                    .getOrElse { throwable ->
                        scLog("$TAG loadSchoolTeachers pageFailure school=$normalizedSchoolId offset=$offset", throwable)
                        throw throwable
                    }
            }.distinctBy { it.id }
        }.onFailure { throwable -> scLog("$TAG loadSchoolTeachers failure school=$normalizedSchoolId", throwable) }
    }

    suspend fun loadTeacherAssignments(
        schoolId: String,
        forceRemote: Boolean = false
    ): Result<List<SchoolStaffWithAssignmentsResponse>> {
        val normalizedSchoolId = schoolId.trim()
        if (normalizedSchoolId.isBlank()) return Result.failure(IllegalArgumentException("schoolId is required"))
        scLog("$TAG loadTeacherAssignments start school=$normalizedSchoolId forceRemote=$forceRemote")
        return runCatching {
            loadPagedItems(
                tag = TAG,
                operation = "loadTeacherAssignments",
                pageSize = PAGE_SIZE,
                maxPages = MAX_PAGES,
                startContext = "school=$normalizedSchoolId"
            ) { offset ->
                KtorApiClient.getSchoolStaffsWithAssignments(
                    schoolId = normalizedSchoolId,
                    isActive = true,
                    limit = PAGE_SIZE,
                    offset = offset
                ).getOrElse { throwable ->
                    scLog("$TAG loadTeacherAssignments pageFailure school=$normalizedSchoolId offset=$offset", throwable)
                    throw throwable
                }
            }.distinctBy { it.id }
        }.onFailure { throwable -> scLog("$TAG loadTeacherAssignments failure school=$normalizedSchoolId", throwable) }
    }

    suspend fun loadSchoolStaffPage(
        schoolId: String,
        query: String = "",
        roleTitle: String? = null,
        isActive: Boolean? = null,
        advancedFilter: SchoolStaffFilterRequest? = null,
        pageIndex: Int = 0,
        pageSize: Int = PaginationPolicy.DEFAULT_PAGE_SIZE,
        sort: ServerSort = ServerSort("employee_id", com.schoolconnect.app.feature.common.ServerSortOrder.ASC),
        forceRemote: Boolean = false
    ): Result<List<SchoolStaffResponse>> {
        val normalizedSchoolId = schoolId.trim()
        if (normalizedSchoolId.isBlank()) {
            return Result.failure(IllegalArgumentException("schoolId is required"))
        }

        val normalizedQuery = query.trim()
        val normalizedRoleTitle = roleTitle?.trim()?.takeIf { it.isNotBlank() }
        val safePageIndex = pageIndex.coerceAtLeast(0)
        val safePageSize = pageSize.coerceAtLeast(1)
        val cacheKey = SchoolStaffPageCacheKey(
            schoolId = normalizedSchoolId,
            query = normalizedQuery,
            roleTitle = normalizedRoleTitle,
            isActive = isActive,
            pageIndex = safePageIndex,
            pageSize = safePageSize,
            sort = sort.cacheKey
        )

        cachedSchoolStaffPage?.takeIf { !forceRemote && advancedFilter == null && it.key == cacheKey }?.let { cache ->
            scLog(
                "$TAG loadSchoolStaffPage cacheHit school=$normalizedSchoolId q=${normalizedQuery.ifBlank { "<blank>" }} roleTitle=${normalizedRoleTitle ?: "<any>"} isActive=${isActive?.toString() ?: "<any>"} pageIndex=$safePageIndex pageSize=$safePageSize count=${cache.staff.size}"
            )
            return returnCachedAndRefresh(scope, TAG, "loadSchoolStaffPage", "school=$normalizedSchoolId pageIndex=$safePageIndex pageSize=$safePageSize", cache.staff) {
                loadSchoolStaffPage(
                    schoolId = normalizedSchoolId,
                    query = query,
                    roleTitle = normalizedRoleTitle,
                    isActive = isActive,
                    advancedFilter = null,
                    pageIndex = safePageIndex,
                    pageSize = safePageSize,
                    sort = sort,
                    forceRemote = true
                )
            }
        }

        val offset = safePageIndex * safePageSize
        scLog(
            "$TAG loadSchoolStaffPage start school=$normalizedSchoolId q=${normalizedQuery.ifBlank { "<blank>" }} roleTitle=${normalizedRoleTitle ?: "<any>"} isActive=${isActive?.toString() ?: "<any>"} pageIndex=$safePageIndex pageSize=$safePageSize offset=$offset forceRemote=$forceRemote"
        )
        return runCatching {
            val page = if (advancedFilter != null) {
                val all = KtorApiClient.filterSchoolStaffs(
                    schoolId = normalizedSchoolId,
                    request = advancedFilter,
                    sortBy = sort.normalizedField,
                    order = sort.order.apiValue,
                    limit = safePageSize,
                    offset = offset
                ).getOrElse { throwable -> throw throwable }
                all.withServerTotalCount(all.serverTotalCountOrNull())
            } else KtorApiClient.getSchoolStaffs(
                schoolId = normalizedSchoolId,
                roleTitle = normalizedRoleTitle,
                query = normalizedQuery.ifBlank { null },
                isActive = isActive,
                limit = safePageSize,
                offset = offset,
                sortBy = sort.normalizedField,
                order = sort.order.apiValue
            ).map { rows ->
                val serverTotal = rows.serverTotalCountOrNull()
                rows.withServerTotalCount(serverTotal)
            }.getOrElse { throwable ->
                scLog(
                    "$TAG loadSchoolStaffPage pageFailure school=$normalizedSchoolId q=${normalizedQuery.ifBlank { "<blank>" }} pageIndex=$safePageIndex pageSize=$safePageSize",
                    throwable
                )
                throw throwable
            }

            val serverTotal = page.serverTotalCountOrNull()
            val deduped = page.distinctBy { it.id }
                .withServerTotalCount(serverTotal)
            // Advanced filters are served by the filter endpoint and are not
            // represented by SchoolStaffPageCacheKey. Do not let a filtered
            // response replace the cache entry for the equivalent unfiltered
            // page; otherwise clearing a role filter can return the previous
            // empty/filtered page without requesting the full list again.
            if (advancedFilter == null) {
                cachedSchoolStaffPage = CachedSchoolStaffPage(cacheKey, deduped)
            }
            scLog(
                "$TAG loadSchoolStaffPage complete school=$normalizedSchoolId q=${normalizedQuery.ifBlank { "<blank>" }} pageIndex=$safePageIndex pageSize=$safePageSize count=${deduped.size}"
            )
            deduped
        }.onFailure { throwable ->
            scLog(
                "$TAG loadSchoolStaffPage failure school=$normalizedSchoolId q=${normalizedQuery.ifBlank { "<blank>" }} pageIndex=$safePageIndex pageSize=$safePageSize",
                throwable
            )
        }
    }

    suspend fun loadSchoolStaffAnalytics(
        schoolId: String,
        forceRemote: Boolean = false
    ): Result<SchoolUsersAnalyticsResponse> {
        val normalizedSchoolId = schoolId.trim()
        if (normalizedSchoolId.isBlank()) {
            return Result.failure(IllegalArgumentException("schoolId is required"))
        }

        val cacheKey = tenantCacheKey("school-staff-analytics:$normalizedSchoolId")
        return loadRefreshableCachedValue(
            scope = scope,
            tag = TAG,
            operation = "loadSchoolStaffAnalytics",
            context = "school=$normalizedSchoolId forceRemote=$forceRemote",
            cachedValue = if (forceRemote) null else cachedAnalytics[cacheKey],
            fetchValue = { KtorApiClient.getUsersAnalytics(normalizedSchoolId) },
            updateCache = { analytics, fetchedAt ->
                cachedAnalytics[cacheKey] = CachedValue(analytics, fetchedAt)
            },
            successDetails = { analytics ->
                "scope=${analytics.scope.ifBlank { "<blank>" }} total=${analytics.metrics.totalUsers} active=${analytics.metrics.activeUsers} inactive=${analytics.metrics.inactiveUsers}"
            }
        )
    }

    suspend fun loadSchoolStaffProfile(
        staffId: String
    ): Result<SchoolStaffResponse> {
        val normalizedStaffId = staffId.trim()
        if (normalizedStaffId.isBlank()) {
            return Result.failure(IllegalArgumentException("staffId is required"))
        }
        scLog("$TAG loadSchoolStaffProfile start staffId=$normalizedStaffId")
        return KtorApiClient.getSchoolStaffProfile(normalizedStaffId)
            .onSuccess { response ->
                scLog(
                    "$TAG loadSchoolStaffProfile success staffId=$normalizedStaffId name=${response.name.orEmpty().ifBlank { "<blank>" }}"
                )
            }
            .onFailure { throwable ->
                scLog("$TAG loadSchoolStaffProfile failure staffId=$normalizedStaffId", throwable)
            }
    }

    suspend fun loadTeacherSchoolStaffProfile(
        schoolId: String,
        userId: String,
        preferredStaffId: String = ""
    ): Result<SchoolStaffResponse> {
        val normalizedSchoolId = schoolId.trim()
        val normalizedUserId = userId.trim()
        if (normalizedSchoolId.isBlank()) {
            return Result.failure(IllegalArgumentException("schoolId is required"))
        }
        if (normalizedUserId.isBlank()) {
            return Result.failure(IllegalArgumentException("userId is required"))
        }

        scLog("$TAG loadTeacherSchoolStaffProfile start school=$normalizedSchoolId user=$normalizedUserId")
        val directCandidates = listOf(normalizedUserId, preferredStaffId.trim())
            .filter { it.isNotBlank() }
            .distinct()
        for (candidate in directCandidates) {
            loadSchoolStaffProfile(candidate).getOrNull()?.let { response ->
                return Result.success(response).also {
                    scLog("$TAG loadTeacherSchoolStaffProfile resolvedDirect school=$normalizedSchoolId staffId=${response.id.orEmpty()}")
                }
            }
        }

        return loadSchoolStaffPage(
            schoolId = normalizedSchoolId,
            roleTitle = null,
            isActive = true,
            pageIndex = 0,
            pageSize = 100,
            forceRemote = true
        ).mapCatching { rows ->
            val staff = rows.firstOrNull { it.userId.orEmpty().trim() == normalizedUserId }
                ?: throw IllegalStateException("Unable to resolve teacher staff profile for the current school context")
            loadSchoolStaffProfile(staff.id.orEmpty()).getOrElse { throw it }
        }.onSuccess { response ->
            scLog("$TAG loadTeacherSchoolStaffProfile success school=$normalizedSchoolId staffId=${response.id.orEmpty()}")
        }.onFailure { throwable ->
            scLog("$TAG loadTeacherSchoolStaffProfile failure school=$normalizedSchoolId user=$normalizedUserId", throwable)
        }
    }

    suspend fun createSchoolStaff(
        request: SchoolStaffCreateRequest
    ): Result<SchoolStaffResponse> {
        val normalizedSchoolId = request.schoolId.trim()
        if (normalizedSchoolId.isBlank()) {
            return Result.failure(IllegalArgumentException("schoolId is required"))
        }
        scLog(
            "$TAG createSchoolStaff start school=$normalizedSchoolId employeeId=${request.employeeId.ifBlank { "<blank>" }} name=${request.name.ifBlank { "<blank>" }}"
        )
        return KtorApiClient.createSchoolStaff(request).onSuccess { response ->
            invalidateCaches()
            scLog(
                "$TAG createSchoolStaff success school=${response.schoolId.orEmpty().ifBlank { normalizedSchoolId }} id=${response.id.orEmpty()} employeeId=${response.employeeId.orEmpty()} active=${response.isActive == true}"
            )
        }.onFailure { throwable ->
            scLog("$TAG createSchoolStaff failure school=$normalizedSchoolId", throwable)
        }
    }

    suspend fun createSchoolStaffWithAssignments(
        request: AddStaffWithAssignmentsRequest,
        currentUserId: String? = null
    ): Result<SchoolStaffResponse> {
        val normalizedSchoolId = request.schoolId.trim()
        if (normalizedSchoolId.isBlank()) {
            return Result.failure(IllegalArgumentException("schoolId is required"))
        }
        scLog(
            "$TAG createSchoolStaffWithAssignments start school=$normalizedSchoolId employeeId=${request.employeeId.ifBlank { "<blank>" }} assignments=${request.teachingAssignments?.size ?: 0}"
        )
        return KtorApiClient.createSchoolStaffWithAssignments(request, currentUserId)
            .onSuccess { response ->
                invalidateCaches()
                scLog(
                    "$TAG createSchoolStaffWithAssignments success school=$normalizedSchoolId id=${response.id.orEmpty()} employeeId=${response.employeeId.orEmpty()} assignments=${request.teachingAssignments?.size ?: 0}"
                )
            }
            .onFailure { throwable ->
                scLog("$TAG createSchoolStaffWithAssignments failure school=$normalizedSchoolId", throwable)
            }
    }

    suspend fun updateSchoolStaff(
        staffId: String,
        request: SchoolStaffUpdateRequest,
        currentUserId: String? = null
    ): Result<SchoolStaffResponse> {
        val normalizedStaffId = staffId.trim()
        if (normalizedStaffId.isBlank()) {
            return Result.failure(IllegalArgumentException("staffId is required"))
        }
        scLog(
            "$TAG updateSchoolStaff start staffId=$normalizedStaffId currentUserId=${currentUserId?.trim().orEmpty().ifBlank { "<blank>" }}"
        )
        return KtorApiClient.updateSchoolStaff(
            staffId = normalizedStaffId,
            request = request,
            currentUserId = currentUserId
        ).onSuccess { response ->
            invalidateCaches()
            scLog(
                "$TAG updateSchoolStaff success staffId=$normalizedStaffId employeeId=${response.employeeId.orEmpty()} active=${response.isActive == true}"
            )
        }.onFailure { throwable ->
            scLog("$TAG updateSchoolStaff failure staffId=$normalizedStaffId", throwable)
        }
    }

    suspend fun loadSchoolStaffWithAssignments(
        staffId: String
    ): Result<SchoolStaffWithAssignmentsResponse> {
        val normalizedStaffId = staffId.trim()
        if (normalizedStaffId.isBlank()) {
            return Result.failure(IllegalArgumentException("staffId is required"))
        }
        scLog("$TAG loadSchoolStaffWithAssignments start staffId=$normalizedStaffId")
        return KtorApiClient.getSchoolStaffWithAssignments(normalizedStaffId)
            .onSuccess { response ->
                scLog(
                    "$TAG loadSchoolStaffWithAssignments success staffId=$normalizedStaffId assignments=${response.teachingAssignments?.size ?: 0} classTeacherAssignments=${response.classTeacherAssignments?.size ?: 0}"
                )
            }
            .onFailure { throwable -> scLog("$TAG loadSchoolStaffWithAssignments failure staffId=$normalizedStaffId", throwable) }
    }

    suspend fun loadCurrentTeacherWithAssignments(): Result<SchoolStaffWithAssignmentsResponse> {
        scLog("$TAG loadCurrentTeacherWithAssignments start")
        return runCatching {
            val profile = KtorApiClient.getCurrentSchoolStaffProfile().getOrElse { throw it }
            val staffId = profile.id?.trim().orEmpty()
            require(staffId.isNotBlank()) { "Current teacher staff ID is missing." }
            loadSchoolStaffWithAssignments(staffId).getOrElse { throw it }
        }.onFailure { throwable -> scLog("$TAG loadCurrentTeacherWithAssignments failure", throwable) }
    }

    suspend fun updateSchoolStaffWithAssignments(
        staffId: String,
        request: UpdateStaffWithAssignmentsRequest,
        currentUserId: String? = null
    ): Result<SchoolStaffWithAssignmentsResponse> {
        val normalizedStaffId = staffId.trim()
        if (normalizedStaffId.isBlank()) {
            return Result.failure(IllegalArgumentException("staffId is required"))
        }
        scLog(
            "$TAG updateSchoolStaffWithAssignments start staffId=$normalizedStaffId assignments=${request.teachingAssignments?.size ?: 0}"
        )
        return KtorApiClient.updateSchoolStaffWithAssignments(normalizedStaffId, request, currentUserId)
            .onSuccess {
                invalidateCaches()
                scLog("$TAG updateSchoolStaffWithAssignments success staffId=$normalizedStaffId")
            }
            .onFailure { throwable -> scLog("$TAG updateSchoolStaffWithAssignments failure staffId=$normalizedStaffId", throwable) }
    }

    suspend fun deleteSchoolStaff(
        staffId: String
    ): Result<Unit> {
        val normalizedStaffId = staffId.trim()
        if (normalizedStaffId.isBlank()) {
            return Result.failure(IllegalArgumentException("staffId is required"))
        }

        scLog("$TAG deleteSchoolStaff start staffId=$normalizedStaffId")
        return KtorApiClient.deleteSchoolStaff(normalizedStaffId).onSuccess {
            invalidateCaches()
            scLog("$TAG deleteSchoolStaff success staffId=$normalizedStaffId")
        }.onFailure { throwable ->
            scLog("$TAG deleteSchoolStaff failure staffId=$normalizedStaffId", throwable)
        }
    }

    suspend fun toggleSchoolStaffStatus(
        staffId: String,
        isActive: Boolean,
        currentUserId: String? = null
    ): Result<SchoolStaffResponse> {
        val normalizedStaffId = staffId.trim()
        if (normalizedStaffId.isBlank()) {
            return Result.failure(IllegalArgumentException("staffId is required"))
        }

        scLog(
            "$TAG toggleSchoolStaffStatus start staffId=$normalizedStaffId isActive=$isActive currentUserId=${currentUserId?.trim().orEmpty().ifBlank { "<blank>" }}"
        )
        return KtorApiClient.updateSchoolStaff(
            staffId = normalizedStaffId,
            request = SchoolStaffUpdateRequest(isActive = isActive),
            currentUserId = currentUserId
        ).onSuccess { response ->
            invalidateCaches()
            scLog(
                "$TAG toggleSchoolStaffStatus success staffId=$normalizedStaffId employeeId=${response.employeeId.orEmpty()} active=${response.isActive == true}"
            )
        }.onFailure { throwable ->
            scLog("$TAG toggleSchoolStaffStatus failure staffId=$normalizedStaffId", throwable)
        }
    }

    fun invalidateCaches() {
        cachedSchoolStaff = null
        cachedSchoolStaffPage = null
        cachedAnalytics.clear()
    }
}
